// @flow
// $FlowFixMe - Cannot import useState because there is no useState export in react.
import React, { useState } from 'react';
import { endOfMonth, format, parse, startOfMonth } from 'date-fns';
import { Mutation, Query } from '@apollo/client/react/components';
import { ClientIdContext, mapDataWithIdAndNameToOptions } from 'bk-web-app/index';
import { defaultDataIdFromObject, gql } from '@apollo/client';
import { toastError, toastSuccess } from 'bench-internal-theme';

import CategorizationSuggestions from './CategorizationSuggestions';
import type { DataEntry, JournalEntry, SuggestedLedger } from './types';
import {
  ASSIGN_DATA_ENTRY_LEDGER_ACCOUNT,
  GET_CATEGORIZATION_LEDGER_ACCOUNTS,
  GET_TRANSACTION_CATEGORIZATIONS,
  UPDATE_TRANSACTION_CATEGORIZATION,
} from './queries';

const maxSuggestedLedgers = 3;

const dateFormat = 'YYYY-MM-DD';

const atStartOfMonth = dateString => format(startOfMonth(parse(dateString)), dateFormat);

const atEndOfMonth = dateString => format(endOfMonth(parse(dateString)), dateFormat);

const showLedgerAccountAssignCompletedToast = (vendorName, ledgerName) =>
  toastSuccess(
    'Categorization completed successfully',
    `Categorized ${vendorName} to ${ledgerName}.`,
  );

const showLedgerAccountAssignErrorToast = error =>
  toastError(
    'Failed to assign ledger account',
    `There was a problem assigning that ledger account. The error was: ${error.message}.`,
  );

const acceptCategorization = (
  acceptedLedger,
  assignDataEntryLedgerAccount,
  clientId,
  dataEntry,
) => {
  const { id: ledgerAccountId } = acceptedLedger.ledgerAccount;
  const updateDataEntriesInput = {
    clientId,
    dataEntryIds: [dataEntry.id],
    ledgerAccountId,
  };

  assignDataEntryLedgerAccount({
    // The cache is manually updated because the mutation response doesn't include enough
    // data for Apollo to update the cache automatically.
    update: (cache: any) => {
      cache.writeFragment({
        id: defaultDataIdFromObject(dataEntry),
        fragment: gql`
          fragment assignDataEntryLedgerAccount on DataEntry {
            ledgerAccount {
              id
            }
            __typename
          }
        `,
        data: {
          ledgerAccount: {
            id: ledgerAccountId,
            __typename: 'LedgerAccount',
          },
          __typename: 'DataEntry',
        },
      });
    },
    variables: { updateDataEntriesInput },
  })
    .then(() =>
      showLedgerAccountAssignCompletedToast(
        dataEntry.journalEntry.vendorName,
        acceptedLedger.ledgerAccount.name,
      ),
    )
    .catch(showLedgerAccountAssignErrorToast);
};

const setUpdatedSuggestedLedgers = (data, setSuggestedLedgers) => {
  const updatedSuggestedLedgers = data?.updateTransactionCategorization?.suggestedLedgers;
  if (updatedSuggestedLedgers) {
    setSuggestedLedgers(updatedSuggestedLedgers);
  }
};

const addSuggestedLedger = (
  journalEntry: JournalEntry,
  selectedLedgerId: string,
  suggestedLedgers: Array<SuggestedLedger>,
  updateTransactionCategorization: any,
  clientId: string,
  setSuggestedLedgers: Function,
) => {
  if (suggestedLedgers.length >= maxSuggestedLedgers) {
    toastError(
      'Maximum number of category suggestions reached',
      `The limit is ${maxSuggestedLedgers}`,
    );
    return;
  }

  const suggestedLedgersIds = [
    ...suggestedLedgers.map(suggestedLedger => suggestedLedger.ledgerAccount.id),
    selectedLedgerId,
  ];

  updateTransactionCategorization({
    update: (
      cache: any,
      { data: { updateTransactionCategorization: transactionCategorization } },
    ) => {
      // If the updated transaction categorization isn't in the list of categorizations for the
      // month then add it to the list so that the query doesn't have to be refetched
      const transactionCategorizationsQuery = {
        query: GET_TRANSACTION_CATEGORIZATIONS,
        variables: {
          clientId,
          startDate: atStartOfMonth(journalEntry.dateOfTransaction),
          endDate: atEndOfMonth(journalEntry.dateOfTransaction),
        },
      };
      const transactionCategorizationsData = cache.readQuery(transactionCategorizationsQuery);
      const { transactionCategorizations } = transactionCategorizationsData;
      if (transactionCategorizations.every(({ id }) => id !== transactionCategorization.id)) {
        cache.writeQuery({
          ...transactionCategorizationsQuery,
          data: {
            ...transactionCategorizationsData,
            transactionCategorizations: [...transactionCategorizations, transactionCategorization],
          },
        });
      }

      // If the journal entry associated with the updated transaction categorization doesn't have a
      // reference to it in the cache, then update that journal entry in the cache to link them
      if (!journalEntry.categorization) {
        cache.writeFragment({
          id: defaultDataIdFromObject(journalEntry),
          fragment: gql`
            fragment addCategorization on JournalEntry {
              categorization {
                id
              }
              __typename
            }
          `,
          data: {
            categorization: {
              id: transactionCategorization.id,
              __typename: 'TransactionCategorization',
            },
            __typename: 'JournalEntry',
          },
        });
      }
    },
    variables: {
      journalEntryId: journalEntry.id,
      transactionCategorizationInput: { suggestedLedgers: suggestedLedgersIds },
    },
  })
    .then(({ data }) => setUpdatedSuggestedLedgers(data, setSuggestedLedgers))
    .catch(() =>
      toastError('Failed to add transaction category suggestion', 'Please try again later'),
    );
};

const deleteSuggestedLedger = (
  deletedSuggestedLedger: SuggestedLedger,
  journalEntryId: string,
  suggestedLedgers: Array<SuggestedLedger>,
  updateTransactionCategorization: any,
  setSuggestedLedgers: Function,
) => {
  const suggestedLedgersIds = suggestedLedgers
    .map(suggestedLedger => suggestedLedger.ledgerAccount.id)
    .filter(ledgerId => ledgerId !== deletedSuggestedLedger.ledgerAccount.id);

  updateTransactionCategorization({
    variables: {
      journalEntryId,
      transactionCategorizationInput: { suggestedLedgers: suggestedLedgersIds },
    },
  })
    .then(({ data }) => setUpdatedSuggestedLedgers(data, setSuggestedLedgers))
    .catch(() =>
      toastError('Failed to delete transaction category suggestion', 'Please try again later'),
    );
};

const editSuggestedLedger = (
  editedSuggestedLedger: SuggestedLedger,
  journalEntryId: string,
  newLedgerId: string,
  suggestedLedgers: Array<SuggestedLedger>,
  updateTransactionCategorization: any,
  setSuggestedLedgers: Function,
) => {
  const suggestedLedgersIds = suggestedLedgers
    .map(suggestedLedger => suggestedLedger.ledgerAccount.id)
    .map(ledgerId =>
      ledgerId === editedSuggestedLedger.ledgerAccount.id ? newLedgerId : ledgerId,
    );

  updateTransactionCategorization({
    variables: {
      journalEntryId,
      transactionCategorizationInput: { suggestedLedgers: suggestedLedgersIds },
    },
  })
    .then(({ data }) => setUpdatedSuggestedLedgers(data, setSuggestedLedgers))
    .catch(() =>
      toastError('Failed to edit transaction category suggestion', 'Please try again later'),
    );
};

type CategorizationSuggestionsContainerProps = {
  dataEntry: DataEntry,
};

const CategorizationSuggestionsContainer = ({
  dataEntry,
}: CategorizationSuggestionsContainerProps) => {
  const { journalEntry } = dataEntry;
  const { categorization } = journalEntry;
  const [suggestedLedgers, setSuggestedLedgers] = useState(
    (categorization && categorization.suggestedLedgers) || [],
  );

  return (
    <ClientIdContext.Consumer>
      {clientId => (
        <Query query={GET_CATEGORIZATION_LEDGER_ACCOUNTS} variables={{ clientId }}>
          {(getCategorizationLedgerAccounts: any) => (
            <Mutation mutation={ASSIGN_DATA_ENTRY_LEDGER_ACCOUNT}>
              {assignDataEntryLedgerAccount => (
                <Mutation mutation={UPDATE_TRANSACTION_CATEGORIZATION}>
                  {updateTransactionCategorization => {
                    const ledgerOptions = mapDataWithIdAndNameToOptions(
                      getCategorizationLedgerAccounts.data.ledgerAccounts,
                    );
                    return (
                      <CategorizationSuggestions
                        defaultCategorizationSuggestions={suggestedLedgers}
                        ledgerOptions={ledgerOptions || []}
                        onAcceptCategorization={acceptedLedger =>
                          acceptCategorization(
                            acceptedLedger,
                            assignDataEntryLedgerAccount,
                            clientId,
                            dataEntry,
                          )
                        }
                        onAddCategorization={selectedLedgerId =>
                          addSuggestedLedger(
                            journalEntry,
                            selectedLedgerId,
                            suggestedLedgers,
                            updateTransactionCategorization,
                            clientId,
                            setSuggestedLedgers,
                          )
                        }
                        onDeleteCategorization={deletedSuggestedLedger =>
                          deleteSuggestedLedger(
                            deletedSuggestedLedger,
                            journalEntry.id,
                            suggestedLedgers,
                            updateTransactionCategorization,
                            setSuggestedLedgers,
                          )
                        }
                        onEditCategorization={(editedSuggestedLedger, selectedLedgerId) =>
                          editSuggestedLedger(
                            editedSuggestedLedger,
                            journalEntry.id,
                            selectedLedgerId,
                            suggestedLedgers,
                            updateTransactionCategorization,
                            setSuggestedLedgers,
                          )
                        }
                      />
                    );
                  }}
                </Mutation>
              )}
            </Mutation>
          )}
        </Query>
      )}
    </ClientIdContext.Consumer>
  );
};

export default CategorizationSuggestionsContainer;
